import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { TransitionButton } from '../actions/TransitionButton';
import { JustifiedAction } from '../actions/JustifiedAction';
import { ExecuteErasureAction } from './ExecuteErasureAction';
import { ErasureDispositionPreview } from './ErasureDispositionPreview';
import type { ErasureRow as Row } from './erasureQueueModel';
import {
  ERASURE_STATUS_COLOR,
  ERASURE_STATUS_LABEL,
  GRACE_PHASE_COLOR,
  GRACE_PHASE_LABEL,
} from './erasureState';

/**
 * One row of the erasure queue (D-erasure). The request, its status, the derived grace-phase chip,
 * the member, and the lifecycle actions where the capability permits. The disposition-plan preview
 * (the blast radius) is always available to view BEFORE any action.
 *
 * Trust is SPLIT (the maker-checker): cancel and reject sit at COMPLIANCE; the irreversible EXECUTE
 * is super_admin-only AND gated on the grace window having elapsed. Cancel / reject / execute route
 * through the chokepoint or the guarded execute handler and are audited; the acting admin is recorded
 * by the audit floor, never stored on the row.
 */
export const ErasureRow = ({ row, now }: { row: Row; now: number }) => {
  const { role } = useAccessContext();
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess({ role, accessibleRegions: [] }, { capability });
  const [showPlan, setShowPlan] = useState(false);

  const iso = new Date(now).toISOString();
  const isOpen = row.status === 'requested';

  return (
    <Card
      variant="outlined"
      data-testid={`erasure-request-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.memberEmail} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={ERASURE_STATUS_COLOR[row.status]}
            label={ERASURE_STATUS_LABEL[row.status]}
            data-testid={`erasure-status-${row.id}`}
          />
          {row.gracePhase && (
            <Chip
              size="small"
              variant="filled"
              color={GRACE_PHASE_COLOR[row.gracePhase]}
              label={GRACE_PHASE_LABEL[row.gracePhase]}
              data-testid={`erasure-grace-${row.id}`}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            requested {row.requestedAt.slice(0, 10)} | grace ends{' '}
            {row.graceEndsAt.slice(0, 10)}
          </Typography>
        </Stack>

        {row.status === 'rejected' && row.rejectionReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
            data-testid={`erasure-rejection-${row.id}`}
          >
            Rejected: {row.rejectionReason}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2 }}
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setShowPlan((v) => !v)}
            data-testid={`erasure-plan-toggle-${row.id}`}
          >
            {showPlan ? 'Hide disposition plan' : 'View disposition plan'}
          </Button>

          {isOpen && can('erasure:cancel') && (
            <TransitionButton
              resource="erasureRequests"
              capability="erasure:cancel"
              id={row.id}
              data={{ status: 'cancelled', cancelledAt: iso }}
              previousData={{
                id: row.id,
                status: 'requested',
                cancelledAt: null,
              }}
              label="Cancel"
              color="inherit"
              confirmTitle="Cancel this erasure request"
              confirmContent="Cancel the request during its grace window. Nothing is erased. Use this for a mistake or a withdrawn request."
              successMessage="Request cancelled"
            />
          )}

          {isOpen && can('erasure:reject') && (
            <JustifiedAction
              resource="erasureRequests"
              capability="erasure:reject"
              id={row.id}
              label="Reject"
              color="warning"
              dialogTitle="Reject this erasure request"
              dialogIntro="Refuse the request where the platform has a legal or legitimate basis to retain the data (a banned or fraud / sanctions held member cannot use erasure to wipe the evidence). Refusing must be defensible, so a reason is required and is recorded."
              buildData={(justification) => ({
                status: 'rejected',
                rejectedAt: iso,
                rejectionReason: justification,
              })}
              previousData={{
                id: row.id,
                status: row.status,
                rejectedAt: null,
                rejectionReason: null,
              }}
              successMessage="Request rejected"
            />
          )}

          {row.executable && can('erasure:execute') && (
            <ExecuteErasureAction
              id={row.id}
              subjectUserId={row.subjectUserId}
              now={now}
            />
          )}
        </Stack>

        {showPlan && (
          <ErasureDispositionPreview subjectUserId={row.subjectUserId} />
        )}
      </CardContent>
    </Card>
  );
};
